import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import duration, { type Duration } from 'dayjs/plugin/duration';

dayjs.extend(duration);

type IntervalType = 'second' | 'minute';

interface CountdownTimerProps {
  remain: string; // 예: "0:2:30"
  intervalType: IntervalType; // "second" or "minute"
  onComplete?: () => void;
}

export function CountdownTimer({ remain, intervalType, onComplete }: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState(dayjs.duration(parseRemain(remain)));
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isCompleteRef = useRef(false);

  useEffect(() => {
    // 초기화
    setTimeLeft(dayjs.duration(parseRemain(remain)));
    isCompleteRef.current = false;

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        const updated = prev.subtract(1, 'second');

        if (updated.asSeconds() <= 0) {
          clearInterval(timerRef.current!);
          if (!isCompleteRef.current) {
            isCompleteRef.current = true;
            if (onComplete) setTimeout(onComplete, 0); // 안전하게 상태 갱신
          }
          return dayjs.duration(0);
        }

        return updated;
      });
    }, 1000);

    return () => clearInterval(timerRef.current!);
  }, [remain, intervalType, onComplete]);

  // 표시 형식 제어
  const display = intervalType === 'minute' ? formatDuration(timeLeft, 'HH:mm') : formatDuration(timeLeft, 'HH:mm:ss');

  return <span>{display}</span>;
}

// 남은 시간을 dayjs.duration으로 변환
function parseRemain(remain: string): { hours: number; minutes: number; seconds: number } {
  const [h, m, s] = remain.split(':').map(Number);
  return { hours: h, minutes: m, seconds: s };
}

// 포맷 처리 함수
function formatDuration(duration: Duration, format: 'HH:mm' | 'HH:mm:ss') {
  const hours = String(duration.hours()).padStart(2, '0');
  const minutes = String(duration.minutes()).padStart(2, '0');
  const seconds = String(duration.seconds()).padStart(2, '0');

  return format === 'HH:mm' ? `${hours}H ${minutes}M` : `${hours}H ${minutes}M ${seconds}S`;
}
